import { EllipsisHorizontalIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { Spinner } from './Animations';
import { cn } from './cn';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './DropdownMenu';

export interface ActionPanelItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  /** Renders the action in a destructive (red) style. */
  danger?: boolean;
}

export interface ActionPanelProps {
  /** Primary actions rendered inline in the pill. */
  actions: ActionPanelItem[];
  /** Secondary actions collapsed under the trailing "…" menu. */
  moreActions?: ActionPanelItem[];
  className?: string;
}

const ActionSpinner = () => (
  <Spinner className="h-4 w-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </Spinner>
);

/**
 * Pill-shaped action bar: primary actions inline, with the rest collapsed under a trailing "…" menu.
 */
export const ActionPanel = ({ actions, moreActions = [], className }: ActionPanelProps) => {
  const hasMore = moreActions.length > 0;
  if (actions.length === 0 && !hasMore) return null;

  return (
    <div
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full border border-border bg-surface p-1 shadow-sm',
        className
      )}
    >
      {actions.map((action) => (
        <button
          key={action.key}
          type="button"
          onClick={action.onClick}
          disabled={action.disabled || action.loading}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border-none bg-transparent px-3 py-1.5 type-button-label transition-colors duration-150 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
            action.danger
              ? 'text-danger-600 hover:bg-surface-hover'
              : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
          )}
        >
          {action.loading ? <ActionSpinner /> : action.icon}
          {action.label}
        </button>
      ))}

      {hasMore && actions.length > 0 && <span className="mx-0.5 h-5 w-px bg-border" aria-hidden />}

      {hasMore && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="More actions"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border-none bg-transparent text-text-secondary hover:bg-surface-hover hover:text-text-primary cursor-pointer transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
            >
              <EllipsisHorizontalIcon className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {moreActions.map((action) => (
              <DropdownMenuItem
                key={action.key}
                onClick={action.onClick}
                disabled={action.disabled}
                className={action.danger ? 'text-danger-600' : undefined}
              >
                {action.icon}
                <span>{action.label}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};
